import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { generatePath } from '@helpers';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useCatalystInsightsList } from '@terminal/react-query-hooks/insights/useCatalystInsights';
import { useEarningsInsightsFilter } from '@terminal/react-query-hooks/insights/useEarningsInsightsFilter';
import { parseEarningsInsights } from '@terminal/react-query-hooks/insights/useEarningsInsightsFilter.utils';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import styles from '@terminal/views/theme-overview/tab-content/overview/top-companies-section/TopCompaniesSection.module.scss';
import { TopCompaniesTable } from '@terminal/views/theme-overview/tab-content/overview/top-companies-table/TopCompaniesTable';
import { startOfDay, subWeeks } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const TOP_FIVE_COMPANIES = 5;

export const TopCompaniesSection = ({
  themeId,
  entityTags,
}: {
  themeId: string;
  entityTags: string[];
}) => {
  const { t } = useTranslation(['widget']);

  const topTags = entityTags.slice(0, TOP_FIVE_COMPANIES);
  const {
    data: dataEntities,
    isLoading: isEntitiesLoading,
    isError: isEntitiesError,
  } = useEntities({
    entityTags: topTags,
  });

  const fromDate = useMemo(() => startOfDay(subWeeks(new Date(), 1)), []);

  const {
    data: catalystInsights,
    isLoading: isCatalystInsightsLoading,
    error: catalystInsightsError,
  } = useCatalystInsightsList(
    {
      entities: topTags,
      categories: ['company'],
      from: fromDate.toISOString(),
    },
    !isEntitiesLoading
  );

  const { data: earningsInsightsData = [] } = useEarningsInsightsFilter({
    body: { entities: topTags, from: fromDate.toISOString() },
    isEnabled: !isEntitiesLoading && topTags.length > 0,
  });

  const dataWithInsights = dataEntities.map((entity, idx) => {
    const companyEarnings = earningsInsightsData.filter(
      e => e.entity_tag === entity.tag
    );

    return {
      company: entity.tag,
      relationship: {
        rank: idx + 1,
      },
      insights:
        catalystInsights?.filter(
          insight => insight.identifier === entity.tag
        ) ?? [],
      earningsInsight: parseEarningsInsights(companyEarnings),
    };
  });

  if (isEntitiesLoading || isCatalystInsightsLoading) {
    return (
      <SkeletonLoader
        className={styles.skeletonWrapper}
        areas={[
          {
            styles: {
              width: '100%',
              height: '100%',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (catalystInsightsError || isEntitiesError) {
    throw new Error(
      catalystInsightsError?.message ||
        'Morgan Stanley > TopCompaniesSection: API error'
    );
  }

  if (!dataWithInsights) {
    return null;
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.sectionTitle}>
          <SectionTitle
            title={t('widget:themeOverview.tabs.overview.topCompanies', {
              count: dataWithInsights.length,
            })}
            linkTo={generatePath({
              basePath: `${appPaths.themeOverviewMorganStanley}/${themeId}`,
              params: {
                [queryKeys.activeSubTab]: 'companies',
              },
            })}
          />
        </div>
        <span className={styles.subtitle}>
          {t('widget:themeOverview.tabs.overview.bySignificanceRank')}
        </span>
      </div>
      <TopCompaniesTable data={dataWithInsights} />
    </div>
  );
};
